<template>
  <div class="select-image-wrapper" :class="{ 'from-iframe': pageType === 'iframe' }">
    <div class="select-image-group">
      <div
        v-for="group in filterGroupList"
        :key="group.group_id"
        :class="['group-item', { all: group.group_id === '' }, { selected: selectedItem.group_id === group.group_id }]"
        @click="changeGroup(group)"
      >
        <span :title="group.name" class="group-item__title">
          <span class="ellipsis">{{ group.name }}</span>
          <span style="flex: 1">({{ group.count }})</span>
        </span>
        <template v-if="group.group_id === ''">
          <a-badge dot :count="groupSearch.searchKey.length" :offset="[-8, 0]">
            <ehf-icon icon="search-line" class="group-search-btn" @click.stop="groupSearch.show = true"></ehf-icon>
          </a-badge>
          <a-input v-show="groupSearch.show" v-model:value="groupSearch.searchKey" placeholder="搜索分组" size="small" class="group-search-input" @click.stop>
            <template #suffix>
              <ehf-icon icon="arrow-right-s-fill" @click.stop="groupSearch.show = false"></ehf-icon>
            </template>
          </a-input>
        </template>
      </div>
    </div>
    <div class="select-image-list">
      <div class="list-header">
        <span style="font-size: 16px">{{ selectedItem.name }}({{ selectedItem.count }})</span>
        <a-input-group compact style="width: auto">
          <a-select v-model:value="searchObj.type" style="width: 85px">
            <a-select-option value="name">图片名</a-select-option>
            <a-select-option value="scale">尺寸</a-select-option>
            <a-select-option value="size">大小</a-select-option>
          </a-select>
          <template v-if="searchObj.type === 'name'">
            <a-input-search v-model:value="searchObj.searchKey" style="width: 200px" placeholder="搜索" enter-button @search="getImageList"></a-input-search>
          </template>
          <template v-else-if="searchObj.type === 'scale'">
            <a-input
              :value="searchObj.scaleWidth"
              style="width: 50px; text-align: center"
              placeholder="宽"
              @change="(e) => onNumChange(e, searchObj, 'scaleWidth')"
            />
            <a-input class="scale-link" placeholder="x" disabled />
            <a-input-search
              :value="searchObj.scaleHeight"
              class="scale-search"
              placeholder="高"
              enter-button
              @change="(e) => onNumChange(e, searchObj, 'scaleHeight')"
              @search="getImageList"
            />
          </template>
          <template v-else-if="searchObj.type === 'size'">
            <a-input-search
              :value="searchObj.sizeValue"
              style="width: 200px"
              placeholder="搜索小于填入值"
              suffix="kb"
              enter-button
              @change="(e) => onNumChange(e, searchObj, 'sizeValue')"
              @search="getImageList"
            ></a-input-search>
          </template>
        </a-input-group>
      </div>
      <div class="list-content">
        <div class="image-item" v-for="(img, i) in imageList" :key="i" @click="imgCheck(img)">
          <img :src="img.url" class="image-item__img" />
          <div class="img-title" :title="img.name + '.' + img.ext_name">
            <div class="img-name">{{ img.name }}</div>
            <div class="img-ext-name">.{{ img.ext_name }}</div>
          </div>
          <div class="img-info">
            <div>{{ handleImageSize(img.size) }}</div>
            <div>{{ img.scale }}</div>
          </div>
          <div class="img-preview" @click.stop="imagePreview(i)">预览</div>
          <a-checkbox class="img-checkbox" :checked="img.checked" @click="stopPropagation" @change="() => imgCheck(img)" />
        </div>
      </div>
      <div class="list-footer">
        <a-pagination
          :showSizeChanger="false"
          size="small"
          v-model:current="page"
          v-model:pageSize="pageSize"
          :total="parseInt(count)"
          :show-total="(total) => `共 ${total} 条`"
          @change="getImageList"
        ></a-pagination>
      </div>
    </div>
  </div>
  <div class="footer" :class="{ 'from-iframe': pageType === 'iframe' }">
    <a-button @click="onCancel">取消</a-button>
    <a-button type="primary" @click="onSubmit">确定</a-button>
  </div>

  <ehf-image-preview ref="imagePreviewRef" />
</template>

<script>
  export default {
    name: 'SelectImage',
  }
</script>

<script setup>
  import { getMaterialList, getMaterialGroupList } from '@/api/system/imageGroup'
  import { getImageType } from '@/utils'
  import { useGetters } from '@/utils/composables'
  import { useStore } from 'vuex'
  import { ref, reactive, computed, nextTick, onBeforeUnmount } from 'vue'
  import { message } from 'ant-design-vue'

  const props = defineProps({
    multiple: {
      type: Boolean,
      default() {
        return false
      },
    },
    edit: {
      type: Boolean,
      default() {
        return false
      },
    },
    max: {
      type: Number,
      default: 10,
    },
    limit: {
      type: Object,
      default() {
        return null
      },
    },
    groupId: {
      type: String,
      default: '',
    },
    limitSize: {
      type: Object,
      default() {
        return {
          width: null,
          height: null,
        }
      },
    },
    maxWidth: {
      type: Number,
      default: null,
    },
    maxHeight: {
      type: Number,
      default: null,
    },
    pageType: {
      type: String,
      default: '',
    },
    acceptFormat: {
      type: Array,
      default() {
        return ['jpg', 'png', 'gif']
      },
    },
    // 是否需要缓存
    needCache: {
      type: Boolean,
      default: false,
    },
  })
  const emit = defineEmits(['cancel', 'select'])
  const { imageUpload: imageUploadCache } = useGetters('components', ['imageUpload']) // 缓存的上传组件数据

  const selectedId = ref(props.groupId) // 当前选中组id
  const selectedItem = ref({
    group_id: '',
    name: '全部',
    count: 0,
  }) // 当前分组
  const groupList = ref([]) // 分组列表
  const imageList = ref([]) // 图片列表
  const page = ref(1) // 当前页
  const pageSize = ref(10) // 页大小
  const count = ref(10) // 总条数
  const groupSearch = reactive({
    show: false,
    searchKey: '',
  }) // 分组搜索
  const searchObj = reactive({
    type: 'name', // name-图片名，scale-尺寸，size-大小
    searchKey: '',
    scaleWidth: '',
    scaleHeight: '',
    sizeValue: '',
  }) // 图片搜索
  const filterGroupList = computed(() => {
    if (groupSearch.searchKey.trim() !== '') {
      return groupList.value.filter((item) => {
        return item.group_id === '' || item.name.indexOf(groupSearch.searchKey) > -1
      })
    }
    return groupList.value
  })

  onBeforeUnmount(() => {
    if (props.needCache) {
      let form = {}
      form.searchObj = searchObj
      form.selectedId = selectedId.value
      form.page = page.value
      setImageUploadCache(form)
    }
  })

  const store = useStore()
  const setImageUploadCache = (options) => store.dispatch('components/setImageUploadCache', options) // 设置组件部分数据缓存
  // 初始化
  const init = () => {
    if (props.needCache && imageUploadCache.value) {
      searchObj.type = imageUploadCache.value.searchObj.type || 'name'
      searchObj.searchKey = imageUploadCache.value.searchObj.searchKey || ''
      searchObj.scaleWidth = imageUploadCache.value.searchObj.scaleWidth || ''
      searchObj.scaleHeight = imageUploadCache.value.searchObj.scaleHeight || ''
      searchObj.sizeValue = imageUploadCache.value.searchObj.sizeValue || ''
      selectedId.value = imageUploadCache.value.selectedId || props.groupId
      page.value = imageUploadCache.value.page || 1
    }
    getGroupList()
  }
  // 获取分组列表
  const getGroupList = () => {
    getMaterialGroupList().then((res) => {
      if (res.code === 0) {
        const groups = res.data
        let countAll = 0
        groups.forEach((v) => {
          countAll += parseInt(v.count)
        })
        groupList.value = [{ group_id: '', name: '全部', count: countAll }, ...groups]
        const item = groupList.value.find((v) => v.group_id === selectedId.value)
        let index = groupList.value.findIndex((v) => v.group_id === selectedId.value)
        if (item) {
          selectedItem.value = item
        } else {
          selectedItem.value = groupList.value[0]
          index = 0
        }
        selectedId.value = selectedItem.value.group_id
        const indexHeight = 40 * (index + 1) + 15
        if (indexHeight > 460) {
          nextTick(() => {
            document.querySelector('.select-image-group').scrollTop = indexHeight - 200
          })
        }
        getImageList()
      }
    })
  }
  // 获取图片列表数据
  const getImageList = () => {
    const body = {
      group_id: selectedId.value, // 空为查全部，0为查未分组
      search_type: searchObj.type,
      page: page.value,
      pageSize: pageSize.value,
    }
    switch (searchObj.type) {
      case 'name':
        body.keyword = searchObj.searchKey // 图片名称关键字查询
        break
      case 'scale': {
        const scale = searchObj.scaleWidth + 'x' + searchObj.scaleHeight
        body.keyword = scale
        break
      }
      case 'size':
        body.keyword = searchObj.sizeValue
        break
    }
    getMaterialList(body).then((res) => {
      if (res.code === 0) {
        count.value = res.data.count
        imageList.value = res.data.list.map((v) => {
          v.checked = false
          return v
        })
      } else {
        imageList.value = []
      }
    })
  }
  // 切换分组
  const changeGroup = (item) => {
    selectedItem.value = item
    selectedId.value = item.group_id
    page.value = 1
    getImageList()
  }
  // 图片选择
  const imgCheck = (img) => {
    let format = img.ext_name || getImageType(img.url || '')
    if (props.acceptFormat.indexOf(format.toLowerCase()) == -1) {
      return message.error(`只能选择 ${props.acceptFormat.join('、')} 格式！`)
    }
    if (props.limit) {
      if (img.size === 0) {
        return message.error('此图片大小未知，无法选择。若要使用，请重新上传。')
      } else if (img.size > props.limit.size) {
        return message.error('所选图片不能大于' + props.limit.text)
      }
    }
    //图片尺寸限制
    let width = parseInt(img.scale.split('x')[0])
    let height = parseInt(img.scale.split('x')[1])
    if (props.limitSize.width && props.limitSize.height) {
      if (props.limitSize.width !== width || props.limitSize.height !== height) {
        return message.error(`所选图片不符合尺寸，请选择 ${props.limitSize.width} * ${props.limitSize.height} 尺寸的图片。`)
      }
    }
    if (props.limitSize.width && !props.limitSize.height) {
      if (props.limitSize.width !== width) {
        return message.error(`所选图片宽度不符合尺寸，请选择宽度为 ${props.limitSize.width} 的图片。`)
      }
    }
    if (props.limitSize.height && !props.limitSize.width) {
      if (props.limitSize.height !== height) {
        return message.error(`所选图片高度不符合尺寸，请选择高度为 ${props.limitSize.height} 的图片。`)
      }
    }
    if (props.maxWidth && props.maxHeight) {
      if (width > props.maxWidth || height > props.maxHeight) {
        return message.error(`所选图片超过最大尺寸，请选择宽和高不超过 ${props.maxWidth} * ${props.maxHeight} 的图片。`)
      }
    }
    if (props.maxWidth && !props.maxHeight) {
      if (width > props.maxWidth) {
        return message.error(`所选图片宽度超过限制，请选择宽度不超过 ${props.maxWidth} 的图片。`)
      }
    }
    if (!props.maxWidth && props.maxHeight) {
      if (height > props.maxHeight) {
        return message.error(`所选图片高度超过限制，请选择高度不超过 ${props.maxHeight} 的图片。`)
      }
    }
    //尺寸限制完毕
    if (!props.multiple || props.edit) {
      if (!img.checked) {
        imageList.value.forEach((v) => {
          v.checked = false
        })
      }
    }
    if (!img.checked) {
      const checkedNum = imageList.value.filter((v) => v.checked).length
      if (checkedNum === props.max) {
        return message.error(`最多选择${props.max}张`)
      }
    }
    img.checked = !img.checked
  }
  // 阻止事件继续传递
  const stopPropagation = (e) => {
    e.stopPropagation()
  }
  // 提交
  const onSubmit = () => {
    let urlList = imageList.value.filter((v) => v.checked).map((v) => v.url)
    if (props.pageType === 'iframe') {
      window.parent.postMessage({ cmd: 'upload_success', url: urlList[0] }, '*')
    } else {
      emit('select', urlList)
    }
  }
  // 取消
  const onCancel = () => {
    if (props.pageType === 'iframe') {
      window.parent.postMessage({ cmd: 'close_upload_modal' }, '*')
    } else {
      emit('cancel')
    }
  }
  // 处理图片大小显示
  const handleImageSize = (size) => {
    if (size === 0) {
      return '未知大小'
    } else {
      let size_kb = size / 1024
      if (size_kb > 1024) {
        return parseFloat(size_kb / 1024).toFixed(1) + 'MB'
      } else {
        return parseFloat(size_kb).toFixed(1) + 'KB'
      }
    }
  }
  // 图片放大预览
  const imagePreviewRef = ref(null)
  const imagePreview = (i) => {
    const imgElList = document.querySelectorAll('.select-image-wrapper .image-item .image-item__img')
    const curImg = imgElList[i]
    imagePreviewRef.value.initPhotoSwipeFromDOM('.select-image-wrapper .image-item .image-item__img', curImg, true)
  }
  // 处理数字输入框内容
  const onNumChange = (e, item, key) => {
    const { value } = e.target
    let reg = /^[0-9]*$/
    if ((!isNaN(value) && reg.test(value)) || value === '') {
      item[key] = value
    }
  }

  init()
</script>

<style lang="scss" scoped>
  .select-image-wrapper {
    display: flex;
    flex-direction: row;
    &.from-iframe {
      .select-image-group {
        height: 500px;
      }
    }
    .select-image-group {
      flex: 1;
      height: 460px;
      background-color: #f5f6f8;
      padding: 15px 10px;
      overflow: auto;
      .group-item {
        cursor: pointer;
        height: 40px;
        line-height: 40px;
        padding: 0 10px 0 20px;
        border-radius: 5px;
        display: flex;
        align-items: center;
        position: relative;
        .group-item__title {
          flex: 1;
          display: flex;
          overflow: hidden;
          .ellipsis {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        }
        .group-search-btn {
          font-size: 15px;
          font-weight: initial;
          cursor: pointer;
          color: #777;
          margin-right: 5px;
        }
        .group-search-input {
          width: auto;
          position: absolute;
          right: 10px;
          left: 10px;
          border: none;
          outline: none;
          z-index: 10;
        }
      }
      .all {
        font-weight: bold;
        padding-left: 10px;
      }
      .selected {
        background-color: #eaecf0;
      }
      .group-item + .group-item {
        margin-top: 5px;
      }
    }
    .select-image-list {
      display: flex;
      flex-direction: column;
      flex: 4;
      height: 460px;
      margin-left: 10px;
      padding: 15px 0 15px 15px;
      .list-header {
        display: flex;
        justify-content: space-between;
        .scale-link {
          width: 30px;
          border-left: 0;
          padding: 4px 0;
          text-align: center;
          pointer-events: none;
          background-color: #fff;
        }
        .scale-search {
          width: 100px;
          text-align: center;
          ::v-deep(.ant-input) {
            border-left: 0;
          }
        }
      }
      .list-content {
        flex: 1;
        margin: 40px -8px 0 -8px;
        display: flex;
        flex-wrap: wrap;
        .image-item {
          margin: 0 8px 30px;
          width: calc(20% - 16px);
          cursor: pointer;
          position: relative;
          .image-item__img {
            width: 100%;
            height: 110px;
            object-fit: cover;
          }
          .img-title {
            width: 100%;
            margin-top: 5px;
            display: flex;
            .img-name {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .img-ext-name {
              flex: 1;
            }
          }
          .img-info {
            width: 100%;
            margin-top: 5px;
            font-size: 12px;
            color: #999;
            display: flex;
            justify-content: space-between;
          }
          .img-preview {
            display: none;
            position: absolute;
            top: 4px;
            left: 4px;
            font-size: 12px;
            color: #fff;
            padding: 2px 5px;
            background-color: rgba(0, 0, 0, 0.3);
            border-radius: 3px;
          }
          .img-checkbox {
            position: absolute;
            top: 4px;
            right: 4px;
          }
          &:hover {
            .img-preview {
              display: block;
            }
          }
        }
      }
      .list-footer {
        .ant-pagination {
          margin-top: 0;
        }
      }
    }
  }
  .footer {
    position: absolute;
    bottom: 20px;
    right: 20px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    &.from-iframe {
      right: 0;
      width: unset;
    }
  }
</style>
